<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card purchase-entry-card">
          <div class="card-body purchase-entry-body">
            <div class="purchase-workspace">

            <!-- HEADER -->
            <section class="purchase-header-card">
              <div class="purchase-header-row purchase-header-row-1">
                <div class="ph-field ph-field-supplier">
                  <label class="ph-label">Supplier Name <span class="ph-req">*</span></label>
                  <div class="lookup-control-row">
                    <div class="lookup-box">
                      <b-form-input
                        v-model="vendorSearchQuery"
                        type="text"
                        placeholder="Search supplier by name or phone"
                        autocomplete="off"
                        class="ph-input"
                        @focus="showVendorResults = true"
                        @input="onVendorLookupInput"
                      />
                      <div v-if="showVendorResults" class="lookup-results">
                        <button
                          v-for="vendor in vendorSearchResults"
                          :key="vendor.vendorid"
                          type="button"
                          class="lookup-result-item"
                          @mousedown.prevent="selectVendor(vendor)"
                        >
                          <span class="lookup-result-title">{{ vendor.vendorname || "Unnamed supplier" }}</span>
                          <span class="lookup-result-meta">{{ vendor.phone || "No phone" }}</span>
                        </button>
                        <div v-if="vendorSearchQuery.trim() && !vendorSearchResults.length" class="lookup-empty-state">
                          No supplier found. Click <strong>Add New</strong> to create one.
                        </div>
                      </div>
                    </div>
                    <button v-if="canAddSuppliers" type="button" class="inline-add-btn" @click="openVendorModal">
                      <i class="fas fa-user-plus"></i><span>Add New</span>
                    </button>
                  </div>
                </div>
                <div class="ph-field">
                  <label class="ph-label">Invoice No</label>
                  <b-form-input v-model="header.invoiceno" placeholder="Supplier invoice no" class="ph-input" :class="{ 'dup-invoice-input': invoiceDuplicate }" />
                  <div v-if="invoiceDuplicate" class="dup-invoice-warning" role="alert">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>{{ invoiceDuplicateText }} <a href="javascript:void(0);" @click="openDuplicatePurchase">View</a></span>
                  </div>
                </div>
                <div class="ph-field">
                  <label class="ph-label">Invoice Date</label>
                  <b-form-input type="date" v-model="header.invoicedate" class="ph-input" />
                </div>
                <div class="ph-field">
                  <label class="ph-label">Internal Bill No</label>
                  <b-form-input v-model="header.billno" class="ph-input" readonly />
                </div>
                <div class="ph-field">
                  <label class="ph-label">Transport Mode</label>
                  <b-form-select v-model="header.transportmode" :options="transportModes" class="ph-input" />
                </div>
                <div class="ph-field">
                  <label class="ph-label">Vehicle No</label>
                  <b-form-input v-model="header.vehicleno" placeholder="e.g. KL01AB1234" class="ph-input" />
                </div>
              </div>
              <div class="purchase-header-row purchase-header-row-2">
                <div class="ph-field">
                  <label class="ph-label">Payment Mode</label>
                  <b-form-select v-model="header.paymentmode" :options="paymentModes" class="ph-input" />
                </div>
                <div class="ph-field ph-field-barcode">
                  <label class="ph-label">Barcode / Quick Search</label>
                  <b-form-input
                    v-model="barcodeSearch"
                    placeholder="Scan barcode or type item name"
                    class="ph-input ph-input-barcode"
                    @keydown.enter.prevent="openQuickAddPanel"
                  />
                </div>
              </div>
            </section>

            <!-- LINE ITEMS -->
            <section class="line-items-card">
              <div class="line-items-table-wrap">
                <table class="line-items-table">
                  <thead>
                    <tr>
                      <th class="col-num">#</th>
                      <th class="col-item">Item Name</th>
                      <th class="col-qty">Qty</th>
                      <th class="col-ptr">PTR</th>
                      <th class="col-net-ptr">Net PTR</th>
                      <th class="col-mrp">MRP</th>
                      <th class="col-wholesale">Wholesale</th>
                      <th class="col-gst">GST</th>
                      <th class="col-disc">Disc%</th>
                      <th class="col-taxable">Taxable</th>
                      <th class="col-tax">Tax</th>
                      <th class="col-total">Row Total</th>
                      <th class="col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody v-if="items.length">
                    <tr v-for="(item, index) in items" :key="index">
                      <td class="col-num">{{ index + 1 }}</td>
                      <td class="col-item">
                        <div class="item-name-cell">
                          <span>{{ item.itemname }}</span>
                          <span v-if="item.amountBased" class="amount-badge">amount-based</span>
                        </div>
                      </td>
                      <td class="col-qty">{{ item.quantity }}</td>
                      <td class="col-ptr">{{ formatNumber(item.purchaseprice) }}</td>
                      <td class="col-net-ptr">{{ formatNumber(netPurchaseRate(item)) }}</td>
                      <td class="col-mrp">{{ formatNumber(item.mrp) }}</td>
                      <td class="col-wholesale">{{ formatNumber(item.wholesaleprice) }}</td>
                      <td class="col-gst">{{ item.taxpercent }}%</td>
                      <td class="col-disc">{{ item.discount }}%</td>
                      <td class="col-taxable">₹ {{ formatNumber(item.taxablevalue) }}</td>
                      <td class="col-tax">₹ {{ formatNumber(item.taxamount) }}</td>
                      <td class="col-total">₹ {{ formatNumber(item.rowtotal) }}</td>
                      <td class="col-actions">
                        <button type="button" class="icon-action-btn" title="Edit" @click="openEditItemPanel(index)">
                          <i class="fas fa-pen"></i>
                        </button>
                        <button type="button" class="icon-action-btn icon-action-danger" title="Delete" @click="removeItem(index)">
                          <i class="fas fa-trash-alt"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="!items.length" class="line-items-empty">
                  <p>No line items yet.</p>
                  <span>Click <strong>+ Add Item</strong> to add your first purchase line.</span>
                </div>
              </div>
              <button type="button" class="add-item-btn" @click="openAddItemPanel">+ Add Item</button>
            </section>

            <section class="purchase-extra-cost-card">
              <div class="extra-cost-head">
                <div>
                  <h4 class="extra-cost-title">Expense / Other Cost</h4>
                  <p class="extra-cost-subtitle">Add freight, loading, handling, or other bill-level cost.</p>
                </div>
                <span class="extra-cost-chip">Used in Net PTR</span>
              </div>
              <div class="extra-cost-grid">
                <div class="extra-cost-field">
                  <label class="form-label">Expense Type</label>
                  <b-form-select
                    v-model="header.purchase_expense.expense_type"
                    :options="purchaseExpenseTypeOptions"
                    class="form-ctrl"
                  />
                </div>
                <div class="extra-cost-field">
                  <label class="form-label">Amount</label>
                  <b-form-input
                    type="number"
                    v-model.number="header.purchase_expense.amount"
                    @input="calcSummary"
                    min="0"
                    step="0.01"
                    class="form-ctrl extra-cost-input"
                  />
                </div>
                <div class="extra-cost-field extra-cost-remarks">
                  <label class="form-label">Remarks</label>
                  <b-form-textarea
                    v-model="header.purchase_expense.remarks"
                    rows="2"
                    placeholder="Freight, loading charge, supplier note..."
                    class="form-ctrl remarks-input"
                  />
                </div>
              </div>
            </section>

            <hr class="divider-bold" />

            <!-- SECTION 3 — TOTALS -->
            <div class="purchase-bottom-grid">
              <div></div>
              <div class="purchase-totals-panel">
                <div class="totals-box">
                  <div class="totals-box-head">
                    <div>
                      <h4 class="totals-box-title">Purchase Totals</h4>
                      <p class="totals-box-subtitle">GST breakup and final payable</p>
                    </div>
                    <span class="totals-box-badge">{{ items.length }} Rows</span>
                  </div>
                  <div class="totals-row">
                    <span class="totals-label">Taxable Value</span>
                    <b-form-input :value="summary.taxableValue.toFixed(2)"
                      disabled class="totals-input totals-input-ro" />
                  </div>
                  <div class="totals-row">
                    <span class="totals-label">Total Amount</span>
                    <b-form-input :value="summary.totalAmount.toFixed(2)"
                      disabled class="totals-input totals-input-ro" />
                  </div>
                  <div class="totals-row">
                    <span class="totals-label">Discount Amount</span>
                    <b-form-input type="number" v-model.number="header.discountamount"
                      @input="calcSummary" min="0" class="totals-input" />
                  </div>
                  <div class="totals-row">
                    <span class="totals-label">CGST</span>
                    <b-form-input :value="summary.cgst.toFixed(2)"
                      disabled class="totals-input totals-input-ro" />
                  </div>
                  <div class="totals-row">
                    <span class="totals-label">SGST</span>
                    <b-form-input :value="summary.sgst.toFixed(2)"
                      disabled class="totals-input totals-input-ro" />
                  </div>
                  <div class="totals-row">
                    <span class="totals-label">IGST</span>
                    <b-form-input :value="summary.igst.toFixed(2)"
                      disabled class="totals-input totals-input-ro" />
                  </div>
                  <div class="totals-row">
                    <span class="totals-label">Total Tax</span>
                    <b-form-input :value="summary.taxAmount.toFixed(2)"
                      disabled class="totals-input totals-input-ro" />
                  </div>
                  <div class="totals-row">
                    <span class="totals-label">Round Off</span>
                    <b-form-input type="number" v-model.number="header.roundoff"
                      @input="calcSummary" class="totals-input" />
                  </div>
                  <div class="totals-row totals-net-row">
                    <span class="totals-net-label">Net Amount</span>
                    <span class="totals-net-val">₹ {{ summary.netAmount.toFixed(2) }}</span>
                  </div>
                  <div v-if="isCreditPurchase" class="credit-payment-box">
                    <div class="totals-row">
                      <span class="totals-label">Paid Now</span>
                      <b-form-input
                        v-model.number="header.paid_amount"
                        type="number"
                        min="0"
                        :max="summary.netAmount"
                        step="0.01"
                        class="totals-input"
                      />
                    </div>
                    <div class="totals-row">
                      <span class="totals-label">Due Date</span>
                      <b-form-input
                        v-model="header.due_date"
                        type="date"
                        class="totals-input"
                      />
                    </div>
                    <div class="totals-row">
                      <span class="totals-label">Pending</span>
                      <strong class="text-warning">₹ {{ pendingCreditAmount.toFixed(2) }}</strong>
                    </div>
                    <div class="totals-row">
                      <span class="totals-label">Status</span>
                      <strong class="credit-status-chip">{{ paymentStatusLabel }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- SECTION 4 — REMARKS -->
            <div class="row mb-4">
              <div class="col-lg-5 col-sm-12">
                <div class="form-group">
                  <label class="form-label">Remarks</label>
                  <b-form-textarea v-model="header.remarks" rows="2"
                    placeholder="Optional remarks..." class="form-ctrl" />
                </div>
              </div>
            </div>

            <!-- SECTION 5 — BUTTONS -->
            <div class="purchase-footer-actions">
              <button @click="submitForms" class="btn-submit">Submit Purchase</button>
              <router-link to="purchaselist" class="btn-cancel-lnk">Cancel</router-link>
            </div>

            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ADD / EDIT ITEM BOTTOM SHEET -->
    <div v-if="showItemPanel" class="item-sheet-backdrop" @click.self="minimizeItemPanel">
      <div class="item-sheet" role="dialog" aria-modal="true">
        <div class="item-sheet-handle"></div>
        <div class="item-sheet-header">
          <h3>{{ editingItemIndex === null ? "Add Item" : "Edit Item" }}</h3>
          <button type="button" class="item-sheet-close" @click="cancelItemPanel" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="item-sheet-body">
          <div class="sheet-field-row sheet-field-row-2">
            <div class="sheet-field">
              <label class="sheet-label">Category</label>
              <b-form-select
                v-model="panelDraft.itemcategory"
                :options="itemCategoryOptions"
                class="sheet-input"
                @change="onPanelCategoryChange"
              />
            </div>
            <div class="sheet-field sheet-field-item">
              <label class="sheet-label">Item Name <span class="ph-req">*</span></label>
              <div class="panel-item-search">
                <b-form-input
                  v-model="panelItemSearch"
                  class="sheet-input"
                  placeholder="Search item in selected category"
                  autocomplete="off"
                  :disabled="!panelDraft.itemcategory"
                  @focus="onPanelItemFocus"
                  @input="onPanelItemSearch"
                />
                <div v-if="panelItemDropdownOpen && panelDraft.itemcategory" class="panel-item-results">
                  <div v-if="panelSearchLoading" class="panel-item-msg">Loading items...</div>
                  <div v-else-if="!panelFilteredItems.length" class="panel-item-msg">No items in this category</div>
                  <button
                    v-for="(opt, idx) in panelFilteredItems"
                    :key="opt.itemid"
                    type="button"
                    class="panel-item-option"
                    :class="{ active: panelHighlightedIndex === idx }"
                    @mousedown.prevent="selectPanelItem(opt)"
                  >
                    {{ opt.itemname }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="amount-toggle-row">
            <div class="amount-toggle-copy">
              <span class="amount-toggle-label">Amount-based calculation</span>
              <span class="amount-toggle-desc">{{ panelModeDescription }}</span>
            </div>
            <label class="amount-toggle-switch">
              <input type="checkbox" v-model="panelAmountBased" @change="onPanelAmountBasedToggle" />
              <span class="amount-toggle-track"><span class="amount-toggle-thumb"></span></span>
            </label>
          </div>

          <div v-if="panelAmountBased" class="amount-info-banner">
            Enter total purchase amount + quantity — PTR will be auto-calculated.
          </div>

          <div v-if="!panelAmountBased" class="sheet-grid-4">
            <div class="sheet-field">
              <label class="sheet-label">Qty</label>
              <b-form-input v-model.number="panelDraft.quantity" type="number" min="0" class="sheet-input" @input="calcPanelDraft" />
            </div>
            <div class="sheet-field">
              <label class="sheet-label">Free Qty</label>
              <b-form-input v-model.number="panelDraft.freequantity" type="number" min="0" class="sheet-input" />
            </div>
            <div class="sheet-field">
              <label class="sheet-label">PTR</label>
              <b-form-input v-model.number="panelDraft.purchaseprice" type="number" min="0" step="0.01" class="sheet-input" @input="calcPanelDraft" />
            </div>
            <div class="sheet-field">
              <label class="sheet-label">MRP</label>
              <b-form-input v-model.number="panelDraft.mrp" type="number" min="0" step="0.01" class="sheet-input" />
            </div>
          </div>

          <div v-else class="sheet-grid-4">
            <div class="sheet-field">
              <label class="sheet-label">Total Amount (₹)</label>
              <b-form-input v-model.number="panelDraft.totalPurchaseAmount" type="number" min="0" step="0.01" class="sheet-input sheet-input-highlight" @input="calcPanelDraft" />
            </div>
            <div class="sheet-field">
              <label class="sheet-label">Qty</label>
              <b-form-input v-model.number="panelDraft.quantity" type="number" min="0" class="sheet-input sheet-input-highlight" @input="calcPanelDraft" />
            </div>
            <div class="sheet-field">
              <label class="sheet-label">PTR (auto)</label>
              <b-form-input :value="formatNumber(panelDraft.purchaseprice)" readonly class="sheet-input sheet-input-readonly" />
            </div>
            <div class="sheet-field">
              <label class="sheet-label">MRP</label>
              <b-form-input v-model.number="panelDraft.mrp" type="number" min="0" step="0.01" class="sheet-input" />
            </div>
          </div>

          <div class="sheet-grid-2">
            <div class="sheet-field">
              <label class="sheet-label">Wholesale Price</label>
              <b-form-input v-model.number="panelDraft.wholesaleprice" type="number" min="0" step="0.01" class="sheet-input" />
            </div>
            <div class="sheet-field sheet-field-inclusive">
              <label class="sheet-label sheet-label-check">
                <b-form-checkbox v-model="panelDraft.istaxinclusive" @change="calcPanelDraft" />
                Tax Inclusive
              </label>
            </div>
          </div>

          <div class="sheet-grid-3 sheet-grid-shared">
            <div class="sheet-field">
              <label class="sheet-label">Disc%</label>
              <b-form-input v-model.number="panelDraft.discount" type="number" min="0" max="100" class="sheet-input" @input="calcPanelDraft" />
            </div>
            <div class="sheet-field">
              <label class="sheet-label">GST%</label>
              <b-form-input v-model.number="panelDraft.taxpercent" type="number" min="0" max="100" class="sheet-input" :disabled="panelDraft.taxtype === 'EXEMPT'" @input="calcPanelDraft" />
            </div>
            <div class="sheet-field">
              <label class="sheet-label">GST Type</label>
              <b-form-select v-model="panelDraft.taxtype" :options="panelTaxTypeOptions" class="sheet-input" @change="onPanelTaxTypeChange" />
            </div>
          </div>

          <div class="sheet-grid-3">
            <div class="sheet-field">
              <label class="sheet-label">Batch</label>
              <b-form-input v-model="panelDraft.batchno" class="sheet-input" placeholder="Batch no." />
            </div>
            <div class="sheet-field">
              <label class="sheet-label">Mfg Date</label>
              <b-form-input v-model="panelDraft.manufacturedate" type="date" class="sheet-input" />
            </div>
            <div class="sheet-field">
              <label class="sheet-label">Exp Date</label>
              <b-form-input v-model="panelDraft.expirydate" type="date" class="sheet-input" />
            </div>
          </div>

          <div class="calc-pills">
            <div class="calc-pill"><span>Gross</span><strong>₹ {{ formatNumber(panelCalc.gross) }}</strong></div>
            <div class="calc-pill"><span>Discount</span><strong>₹ {{ formatNumber(panelCalc.discount) }}</strong></div>
            <div class="calc-pill"><span>Tax</span><strong>₹ {{ formatNumber(panelCalc.tax) }}</strong></div>
            <div class="calc-pill calc-pill-net"><span>Net</span><strong>₹ {{ formatNumber(panelCalc.net) }}</strong></div>
          </div>
        </div>

        <div class="item-sheet-footer">
          <button type="button" class="sheet-btn-cancel" @click="cancelItemPanel">Cancel</button>
          <button type="button" class="sheet-btn-primary" @click="commitItemPanel">
            {{ editingItemIndex === null ? "Add to Invoice" : "Update Item" }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showVendorModal"
      class="quick-create-modal-backdrop"
      @click.self="closeVendorModal"
    >
      <div class="quick-create-modal" role="dialog" aria-modal="true" aria-labelledby="supplier-quick-create-title">
        <div class="quick-create-modal-header">
          <div>
            <h5 id="supplier-quick-create-title" class="quick-create-modal-title">Add New Supplier</h5>
            <p class="quick-create-modal-subtitle">Supplier ID will be created automatically.</p>
          </div>
          <button type="button" class="quick-create-close" @click="closeVendorModal" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Supplier Name</label>
            <b-form-input
              ref="vendorModalNameInput"
              v-model="vendorModalForm.vendorname"
              type="text"
              placeholder="Enter supplier name"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">Contact Person</label>
            <b-form-input
              v-model="vendorModalForm.contactperson"
              type="text"
              placeholder="Enter contact person"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">Phone</label>
            <InternationalPhoneInput
              ref="vendorModalPhoneInput"
              v-model="vendorModalForm.phone"
              :submitted="vendorModalPhoneSubmitted"
              input-class="form-ctrl"
              placeholder="Enter phone number"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">Email</label>
            <b-form-input
              v-model="vendorModalForm.email"
              type="email"
              placeholder="Enter email address"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">GSTIN</label>
            <b-form-input
              v-model="vendorModalForm.gstin"
              type="text"
              maxlength="15"
              placeholder="Enter GSTIN"
            />
          </div>
          <div class="col-12">
            <label class="form-label">Address</label>
            <b-form-textarea
              v-model="vendorModalForm.address"
              rows="3"
              placeholder="Enter supplier address"
            />
          </div>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn btn-light" @click="closeVendorModal">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="vendorModalSaving" @click="createVendorFromModal">
            {{ vendorModalSaving ? "Saving..." : "Save Supplier" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import InternationalPhoneInput from "@/components/InternationalPhoneInput.vue";
import { confirmIfDuplicateInvoice, duplicateInvoiceMessage, findDuplicateSupplierInvoice } from "@/services/purchaseInvoiceCheck";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";

const emptyItem = () => ({
  itemcategory:    "",
  itemid:          "",
  itemname:        "",
  quantity:        0,
  freequantity:    0,
  purchaseprice:   0,
  mrp:             0,
  wholesaleprice:  0,
  discount:        0,
  baseamount:      0,
  discountvalue:   0,
  taxtype:         "GST",
  taxpercent:      0,
  taxablevalue:    0,
  cgst:            0,
  sgst:            0,
  igst:            0,
  istaxinclusive:  true,
  taxamount:       0,
  batchno:         "",
  manufacturedate: "",
  expirydate:      "",
  rowtotal:             0,
  amountBased:          false,
  totalPurchaseAmount:  0,
});

export default {
  name: "addpurchase",
  components: { InternationalPhoneInput },

  data() {
    const today = moment().format("YYYY-MM-DD");
    return {
      invoiceDuplicate: null,
      invoiceCheckTimer: null,
      todayStr:  today,
      title:     "Add Purchase",
      title1:    "Create new purchase",
      categoriesFromApi: [],
      barcodeSearch: "",
      vendorLookupTimer: null,
      vendorSearchQuery: "",
      showVendorResults: false,
      showVendorModal: false,
      vendorModalSaving: false,

      showItemPanel: false,
      editingItemIndex: null,
      panelDraft: emptyItem(),
      panelAmountBased: false,
      panelCalc: { gross: 0, discount: 0, tax: 0, net: 0 },
      panelItemSearch: "",
      panelItemDropdownOpen: false,
      panelFilteredItems: [],
      panelHighlightedIndex: -1,
      panelSearchLoading: false,
      _panelSearchTimer: null,
      panelDraftCache: null,
      panelTaxTypeOptions: [
        { value: "GST", text: "GST" },
        { value: "IGST", text: "IGST" },
        { value: "EXEMPT", text: "Exempt" },
      ],

      header: {
        vendor:         null,
        locationid:     this.$store.getters.getCurrentLocation?.locationid || "",
        billno:         "",
        invoiceno:      "",
        invoicedate:    today,
        transportmode:  "",
        vehicleno:      "",
        paymentmode:    "Cash",
        paid_amount:    0,
        due_date:       "",
        discountamount: 0,
        roundoff:       0,
        miscellaneous_amount: 0,
        purchase_expense: {
          expense_type: "FREIGHT",
          amount: 0,
          allocation_method: "VALUE",
          remarks: "",
        },
        remarks:        "",
      },

      items:   [],
      summary: { totalAmount: 0, taxableValue: 0, cgst: 0, sgst: 0, igst: 0, taxAmount: 0, netAmount: 0 },
      shopStateCode: null,

      vendorListFromApi: [],
      vendorModalForm: {
        vendorname: "",
        contactperson: "",
        address: "",
        email: "",
        phone: "",
        gstin: "",
      },
      vendorModalPhoneSubmitted: false,
      paymentModes: ["Cash", "Card", "UPI", "Bank Transfer", "Credit"],
      purchaseExpenseTypeOptions: [
        { value: "FREIGHT", text: "Freight" },
        { value: "LOADING", text: "Loading" },
        { value: "HANDLING", text: "Handling" },
        { value: "PACKING", text: "Packing" },
        { value: "TRANSPORT", text: "Transport" },
        { value: "MISCELLANEOUS", text: "Miscellaneous" },
      ],
      transportModes: [
        { value: "", text: "Select Mode" },
        { value: "Road",          text: "Road" },
        { value: "Rail",          text: "Rail" },
        { value: "Air",           text: "Air" },
        { value: "Ship",          text: "Ship" },
        { value: "Hand Delivery", text: "Hand Delivery" },
        { value: "Courier",       text: "Courier" },
        { value: "Other",         text: "Other" },
      ],
    };
  },
  computed: {
    invoiceDuplicateText() {
      return duplicateInvoiceMessage(this.invoiceDuplicate);
    },
    canAddSuppliers() {
      return this.$store.getters.hasPermission("supplier.add");
    },
    isInterState() {
      if (!this.selectedVendorGSTIN || !this.shopStateCode) return false;
      const vendorState = this.selectedVendorGSTIN.substring(0, 2);
      return vendorState !== this.shopStateCode;
    },
    selectedVendorGSTIN() {
      const vendor = this.vendorListFromApi.find(v => v.vendorid === this.header.vendor);
      return vendor?.gstin || '';
    },
    showInterstateIndicator() {
      return false;
    },
    itemCategoryOptions() {
      return [
        { value: "", text: "Select category" },
        ...this.categoriesFromApi.map((category) => ({
          value: category.categoryid,
          text: category.categoryname,
        })),
      ];
    },
    locationOptions() {
      return (this.$store.getters.getLocations || []).map((location) => ({
        value: location.locationid,
        text: location.locationname,
      }));
    },
    vendorSearchResults() {
      return this.getRankedLookupResults(this.vendorListFromApi, this.vendorSearchQuery, [
        "vendorname",
        "phone",
      ]).slice(0, 8);
    },
    isCreditPurchase() {
      return this.header.paymentmode === "Credit";
    },
    pendingCreditAmount() {
      if (!this.isCreditPurchase) return 0;
      return Math.max(0, Number((this.summary.netAmount - (parseFloat(this.header.paid_amount) || 0)).toFixed(2)));
    },
    paymentStatusLabel() {
      if (!this.isCreditPurchase) return "PAID";
      const paidAmount = parseFloat(this.header.paid_amount) || 0;
      if (paidAmount <= 0) return "UNPAID";
      if (paidAmount >= this.summary.netAmount) return "PAID";
      return "PARTIAL";
    },
    panelModeDescription() {
      return this.panelAmountBased
        ? "Enter total amount and qty — PTR is derived automatically."
        : "Enter qty and unit price — totals are calculated forward.";
    },
  },
  watch: {
    "header.invoiceno"() {
      this.scheduleInvoiceCheck();
    },
    "header.vendor"(newVendorId) {
      this.scheduleInvoiceCheck();
      const vendor = this.vendorListFromApi.find((entry) => entry.vendorid === newVendorId);
      this.vendorSearchQuery = vendor ? this.buildVendorLabel(vendor) : "";
      this.showVendorResults = false;
      // Recalculate all rows when vendor changes (inter-state may change)
      this.items.forEach(item => this.calcRow(item));
    },
    "header.paymentmode"(value) {
      if (value !== "Credit") {
        this.header.paid_amount = 0;
        this.header.due_date = "";
      }
    },
    "header.invoicedate"() {
      this.fetchNextBillNo();
    },
  },

  created() {
    this.header.locationid = this.header.locationid || this.$store.getters.getCurrentLocation?.locationid || "";
    this.fetchCategoryListFromApi();
    this.fetchShopStateCode();
    this.fetchNextBillNo();
  },

  mounted() {
    document.addEventListener("keydown",  this.handleKeydown);
    document.addEventListener("mousedown", this.handleOutsideClick);
  },

  beforeDestroy() {
    document.removeEventListener("keydown",  this.handleKeydown);
    document.removeEventListener("mousedown", this.handleOutsideClick);
    clearTimeout(this._panelSearchTimer);
    clearTimeout(this.vendorLookupTimer);
  },

  methods: {
    async fetchNextBillNo() {
      if (!this.header.invoicedate) return;
      try {
        const response = await axios.get("/v1/purchases/next-billno", {
          params: { invoicedate: this.header.invoicedate },
        });
        this.header.billno = response.data?.billno || "";
      } catch (error) {
        console.error("Error fetching next purchase bill number:", error);
      }
    },

    getEmptyVendorModalForm() {
      return {
        vendorname: "",
        contactperson: "",
        address: "",
        email: "",
        phone: "",
        gstin: "",
      };
    },
    normalizeLookupValue(value) {
      return `${value ?? ""}`.trim().toLowerCase();
    },
    getRankedLookupResults(list, query, fields) {
      const normalizedQuery = this.normalizeLookupValue(query);
      if (!normalizedQuery) {
        return list.slice(0, 8);
      }

      return list
        .map((entry) => {
          const values = fields
            .map((field) => this.normalizeLookupValue(entry[field]))
            .filter(Boolean);

          let score = -1;
          if (values.some((value) => value === normalizedQuery)) {
            score = 0;
          } else if (values.some((value) => value.startsWith(normalizedQuery))) {
            score = 1;
          } else if (values.some((value) => value.includes(normalizedQuery))) {
            score = 2;
          }

          return { entry, score };
        })
        .filter((item) => item.score >= 0)
        .sort((a, b) => {
          if (a.score !== b.score) return a.score - b.score;
          return this.normalizeLookupValue(a.entry.vendorname).localeCompare(
            this.normalizeLookupValue(b.entry.vendorname)
          );
        })
        .map((item) => item.entry);
    },
    buildVendorLabel(vendor) {
      const parts = [vendor.vendorname || "Unnamed supplier"];
      if (vendor.phone !== null && vendor.phone !== undefined && `${vendor.phone}` !== "") {
        parts.push(`${vendor.phone}`);
      }
      return parts.join(" | ");
    },
    onVendorLookupInput() {
      this.showVendorResults = true;
      if (!this.vendorSearchQuery.trim()) {
        this.header.vendor = null;
        this.vendorListFromApi = [];
        return;
      }
      clearTimeout(this.vendorLookupTimer);
      this.vendorLookupTimer = setTimeout(() => {
        this.fetchVendorListFromApi(this.vendorSearchQuery.trim());
      }, 250);
    },
    selectVendor(vendor) {
      this.header.vendor = vendor.vendorid;
      this.showVendorResults = false;
    },
    getPrefillVendorFromSearch() {
      const value = this.vendorSearchQuery.trim();
      if (!value) {
        return {
          vendorname: "",
          phone: "",
        };
      }
      const isPhoneLike = /^[\d+\s()-]+$/.test(value);
      return {
        vendorname: isPhoneLike ? "" : value,
        phone: isPhoneLike ? value : "",
      };
    },
    openVendorModal() {
      const prefill = this.getPrefillVendorFromSearch();
      this.vendorModalForm = {
        ...this.getEmptyVendorModalForm(),
        vendorname: prefill.vendorname,
        phone: prefill.phone,
      };
      this.vendorModalPhoneSubmitted = false;
      this.showVendorModal = true;
      this.$nextTick(() => {
        const input = this.$refs.vendorModalNameInput;
        if (input?.focus) input.focus();
      });
    },
    closeVendorModal() {
      this.showVendorModal = false;
      this.vendorModalSaving = false;
      this.vendorModalForm = this.getEmptyVendorModalForm();
      this.vendorModalPhoneSubmitted = false;
    },
    async createVendorFromModal() {
      if (!this.vendorModalForm.vendorname?.trim()) {
        Swal.fire({ icon: "warning", title: "Supplier name is required" });
        return;
      }
      this.vendorModalPhoneSubmitted = true;
      if (!this.$refs.vendorModalPhoneInput?.validate()) {
        Swal.fire({ icon: "warning", title: "Invalid phone number" });
        return;
      }

      this.vendorModalSaving = true;
      try {
        const rawPhone = this.vendorModalForm.phone?.trim() || "";
        const payload = {
          vendorname: this.vendorModalForm.vendorname.trim(),
          contactperson: this.vendorModalForm.contactperson?.trim() || null,
          address: this.vendorModalForm.address?.trim() || null,
          email: this.vendorModalForm.email?.trim() || null,
          phone: rawPhone || null,
          gstin: this.vendorModalForm.gstin?.trim() || null,
        };

        const response = await axios.post("/v1/vendors", payload);
        const vendor = response.data;
        this.vendorListFromApi = [
          { ...vendor, vendorlabel: this.buildVendorLabel(vendor) },
          ...this.vendorListFromApi.filter((entry) => entry.vendorid !== vendor.vendorid),
        ];
        this.header.vendor = vendor.vendorid;
        this.closeVendorModal();
        Swal.fire({ icon: "success", title: "Supplier created", timer: 1400, showConfirmButton: false });
      } catch (error) {
        this.vendorModalSaving = false;
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to create supplier." });
      }
    },

    handleOutsideClick(e) {
      const vendorLookup = this.$el?.querySelector(".lookup-box");
      if (vendorLookup && !vendorLookup.contains(e.target)) {
        this.showVendorResults = false;
      }
      const panelSearch = this.$el?.querySelector(".panel-item-search");
      if (panelSearch && !panelSearch.contains(e.target)) {
        this.panelItemDropdownOpen = false;
      }
    },

    formatNumber(value) {
      return Number(value || 0).toFixed(2);
    },
    formatMoney(value) {
      return `Rs. ${this.formatNumber(value)}`;
    },

    toBoolean(value, defaultValue = true) {
      if (value === true || value === 1 || value === "1" || value === "true") return true;
      if (value === false || value === 0 || value === "0" || value === "false") return false;
      return defaultValue;
    },

    hasPanelDraftContent() {
      const draft = this.panelDraft;
      return !!(
        draft.itemid ||
        draft.itemname ||
        draft.itemcategory ||
        draft.quantity > 0 ||
        draft.purchaseprice > 0 ||
        draft.totalPurchaseAmount > 0 ||
        draft.batchno ||
        draft.manufacturedate ||
        draft.expirydate ||
        this.panelItemSearch?.trim()
      );
    },

    savePanelDraftCache() {
      if (!this.hasPanelDraftContent()) return;
      this.panelDraftCache = {
        panelDraft: JSON.parse(JSON.stringify(this.panelDraft)),
        panelAmountBased: this.panelAmountBased,
        panelItemSearch: this.panelItemSearch,
        panelCalc: { ...this.panelCalc },
        editingItemIndex: this.editingItemIndex,
      };
    },

    restorePanelDraftCache() {
      if (!this.panelDraftCache) return false;
      const cached = this.panelDraftCache;
      this.panelDraft = JSON.parse(JSON.stringify(cached.panelDraft));
      this.panelDraft.istaxinclusive = this.toBoolean(this.panelDraft.istaxinclusive, true);
      this.panelAmountBased = cached.panelAmountBased;
      this.panelItemSearch = cached.panelItemSearch || "";
      this.panelCalc = { ...cached.panelCalc };
      this.editingItemIndex = cached.editingItemIndex;
      this.panelItemDropdownOpen = false;
      return true;
    },

    resetPanelDraft() {
      this.panelDraft = emptyItem();
      this.panelDraft.istaxinclusive = true;
      this.panelAmountBased = false;
      this.panelItemSearch = "";
      this.panelItemDropdownOpen = false;
      this.panelFilteredItems = [];
      this.panelHighlightedIndex = -1;
      this.panelCalc = { gross: 0, discount: 0, tax: 0, net: 0 };
    },

    openAddItemPanel() {
      if (this.panelDraftCache && this.panelDraftCache.editingItemIndex === null) {
        this.restorePanelDraftCache();
        this.editingItemIndex = null;
      } else {
        this.editingItemIndex = null;
        this.resetPanelDraft();
      }
      this.showItemPanel = true;
      if (this.panelDraft.itemcategory) {
        this.fetchPanelItemsByCategory(this.panelDraft.itemcategory);
      }
    },

    openEditItemPanel(index) {
      const source = this.items[index];
      if (!source) return;
      this.editingItemIndex = index;
      this.panelDraft = JSON.parse(JSON.stringify(source));
      this.panelDraft.istaxinclusive = this.toBoolean(this.panelDraft.istaxinclusive, true);
      this.panelAmountBased = false;
      this.panelItemSearch = source.itemname || "";
      this.showItemPanel = true;
      this.calcPanelDraft();
    },

    async openQuickAddPanel() {
      this.openAddItemPanel();
      if (!this.barcodeSearch?.trim()) return;
      this.panelItemSearch = this.barcodeSearch.trim();
      await this.searchPanelItems(this.panelItemSearch);
      this.panelItemDropdownOpen = true;
      if (this.panelFilteredItems.length === 1) {
        await this.selectPanelItem(this.panelFilteredItems[0]);
      }
    },

    closeItemPanel(discard = false) {
      if (!discard) {
        this.savePanelDraftCache();
      } else {
        this.panelDraftCache = null;
        this.resetPanelDraft();
      }
      this.showItemPanel = false;
      this.editingItemIndex = null;
      this.panelItemDropdownOpen = false;
    },

    minimizeItemPanel() {
      this.closeItemPanel(false);
    },

    cancelItemPanel() {
      this.closeItemPanel(true);
    },

    onPanelAmountBasedToggle() {
      if (this.panelAmountBased) {
        const qty = parseFloat(this.panelDraft.quantity) || 0;
        const ptr = parseFloat(this.panelDraft.purchaseprice) || 0;
        this.panelDraft.totalPurchaseAmount = parseFloat((qty * ptr).toFixed(2));
      }
      this.calcPanelDraft();
    },

    async onPanelCategoryChange() {
      this.panelDraft.itemid = "";
      this.panelDraft.itemname = "";
      this.panelItemSearch = "";
      this.panelFilteredItems = [];
      this.panelHighlightedIndex = -1;
      await this.fetchPanelItemsByCategory(this.panelDraft.itemcategory);
      this.panelItemDropdownOpen = true;
    },

    onPanelItemFocus() {
      if (!this.panelDraft.itemcategory) return;
      this.panelItemDropdownOpen = true;
      if (!this.panelFilteredItems.length) {
        this.fetchPanelItemsByCategory(this.panelDraft.itemcategory);
      }
    },

    onPanelTaxTypeChange() {
      if (this.panelDraft.taxtype === "EXEMPT") {
        this.panelDraft.taxpercent = 0;
      }
      this.calcPanelDraft();
    },

    onPanelItemSearch() {
      if (!this.panelDraft.itemcategory) return;
      this.panelItemDropdownOpen = true;
      const query = this.panelItemSearch.trim();
      clearTimeout(this._panelSearchTimer);
      this._panelSearchTimer = setTimeout(() => {
        if (!query) {
          this.fetchPanelItemsByCategory(this.panelDraft.itemcategory);
        } else {
          this.searchPanelItems(query);
        }
      }, 250);
    },

    async fetchPanelItemsByCategory(categoryId) {
      if (!categoryId) {
        this.panelFilteredItems = [];
        return;
      }
      this.panelSearchLoading = true;
      try {
        const res = await axios.get("/v1/items", { params: { category: categoryId } });
        const data = Array.isArray(res.data) ? res.data : res.data.items ?? [];
        this.panelFilteredItems = data.filter((item) => !item.categoryid || item.categoryid === categoryId);
        this.panelHighlightedIndex = this.panelFilteredItems.length ? 0 : -1;
      } catch (e) {
        console.error("Error fetching category items:", e);
        this.panelFilteredItems = [];
      } finally {
        this.panelSearchLoading = false;
      }
    },

    async searchPanelItems(search) {
      const categoryId = this.panelDraft.itemcategory;
      if (!categoryId) return;
      this.panelSearchLoading = true;
      try {
        const res = await axios.get("/v1/items", {
          params: { category: categoryId, search },
        });
        const data = Array.isArray(res.data) ? res.data : res.data.items ?? [];
        this.panelFilteredItems = data.filter((item) => !item.categoryid || item.categoryid === categoryId);
        this.panelHighlightedIndex = this.panelFilteredItems.length ? 0 : -1;
      } catch (e) {
        console.error("Panel search error:", e);
      } finally {
        this.panelSearchLoading = false;
      }
    },

    async selectPanelItem(opt) {
      try {
        const res = await axios.get(`/v1/items/${opt.itemid}`);
        const data = res?.data?.itemid ? res.data : opt;
        this.applyItemDetails(this.panelDraft, data);
        if (!this.panelDraft.itemcategory) {
          this.panelDraft.itemcategory = data.itemcategory || data.categoryid || opt.categoryid || "";
        }
        this.panelItemSearch = this.panelDraft.itemname;
        this.panelItemDropdownOpen = false;
        this.calcPanelDraft();
      } catch (e) {
        console.error("Error fetching panel item:", e);
        this.applyItemDetails(this.panelDraft, opt);
        this.panelItemSearch = this.panelDraft.itemname;
        this.panelItemDropdownOpen = false;
        this.calcPanelDraft();
      }
    },

    calcPanelDraft() {
      const qty = parseFloat(this.panelDraft.quantity) || 0;
      const discPct = Math.min(100, Math.max(0, parseFloat(this.panelDraft.discount) || 0));
      const taxPct = Math.min(100, Math.max(0, parseFloat(this.panelDraft.taxpercent) || 0));
      this.panelDraft.discount = discPct;
      this.panelDraft.taxpercent = taxPct;

      let gross = 0;
      if (this.panelAmountBased) {
        gross = parseFloat(this.panelDraft.totalPurchaseAmount) || 0;
        this.panelDraft.purchaseprice = qty > 0 ? parseFloat((gross / qty).toFixed(4)) : 0;
      } else {
        gross = qty * (parseFloat(this.panelDraft.purchaseprice) || 0);
        this.panelDraft.totalPurchaseAmount = parseFloat(gross.toFixed(2));
      }

      const discAmt = (gross * discPct) / 100;
      const afterDiscount = gross - discAmt;
      let tax = 0;
      let net = afterDiscount;

      if (this.panelDraft.taxtype !== "EXEMPT" && taxPct > 0) {
        if (this.panelDraft.istaxinclusive) {
          const taxable = afterDiscount / (1 + taxPct / 100);
          tax = afterDiscount - taxable;
          net = afterDiscount;
        } else {
          tax = (afterDiscount * taxPct) / 100;
          net = afterDiscount + tax;
        }
      }

      this.panelCalc = {
        gross: parseFloat(gross.toFixed(2)),
        discount: parseFloat(discAmt.toFixed(2)),
        tax: parseFloat(tax.toFixed(2)),
        net: parseFloat(net.toFixed(2)),
      };
    },

    commitItemPanel() {
      if (!this.panelDraft.itemcategory) {
        Swal.fire({ icon: "warning", title: "Category Required", text: "Please select a category." });
        return;
      }
      if (!this.panelDraft.itemid) {
        Swal.fire({ icon: "warning", title: "Item Required", text: "Please select an item." });
        return;
      }
      if (parseFloat(this.panelDraft.quantity) <= 0) {
        Swal.fire({ icon: "warning", title: "Invalid Quantity", text: "Quantity must be greater than 0." });
        return;
      }
      if (this.panelAmountBased && parseFloat(this.panelDraft.totalPurchaseAmount) <= 0) {
        Swal.fire({ icon: "warning", title: "Invalid Amount", text: "Total amount must be greater than 0." });
        return;
      }
      if (this.panelDraft.manufacturedate && this.panelDraft.expirydate && this.panelDraft.expirydate < this.panelDraft.manufacturedate) {
        Swal.fire({ icon: "warning", title: "Invalid Dates", text: "Expiry cannot be before manufacture date." });
        return;
      }

      const item = JSON.parse(JSON.stringify(this.panelDraft));
      item.amountBased = this.panelAmountBased;
      if (item.istaxinclusive === undefined || item.istaxinclusive === null) {
        item.istaxinclusive = true;
      }
      this.calcRow(item);

      if (this.editingItemIndex === null) {
        if (this.items.length >= 20) {
          Swal.fire({ icon: "warning", title: "Limit Reached", text: "Maximum 20 items per purchase." });
          return;
        }
        this.items.push(item);
      } else {
        this.items.splice(this.editingItemIndex, 1, item);
      }

      this.calcSummary();
      this.panelDraftCache = null;
      this.closeItemPanel(true);
      this.barcodeSearch = "";
    },

    applyItemDetails(item, data) {
      const previousItemId = item.itemid || "";
      const nextItemId = data.itemid || "";
      const isNewItemSelection = nextItemId !== previousItemId;

      item.itemid         = nextItemId;
      item.itemname       = data.itemname || "";
      item.itemcategory   = data.categoryid || data.itemcategory || item.itemcategory || "";
      item.purchaseprice  = data.purchaserate   ?? data.purchaseprice   ?? data.purchase_price ?? 0;
      item.mrp            = data.mrp            ?? 0;
      item.wholesaleprice = data.wholesalerate  ?? data.wholesaleprice  ?? data.wholesale_price ?? 0;
      item.taxpercent     = data.taxpercent     ?? data.tax_percent     ?? 0;
      const taxType       = data.taxtype        ?? data.tax_type        ?? item.taxtype ?? "GST";
      item.taxtype        = taxType === "IGST" || (this.isInterState && taxType === "GST") ? "IGST" : taxType;
      if (this.showItemPanel) {
        item.istaxinclusive = isNewItemSelection
          ? true
          : this.toBoolean(item.istaxinclusive, true);
      } else {
        item.istaxinclusive = this.toBoolean(
          data.istaxinclusive ?? data.is_tax_inclusive,
          true
        );
      }
      item.batchno        = data.batchno        ?? item.batchno ?? "";
      item.expirydate     = data.expirydate     ?? item.expirydate ?? "";
      item.manufacturedate = data.manufacturedate ?? item.manufacturedate ?? "";
      if (item.quantity > 0) {
        item.totalPurchaseAmount = parseFloat((item.quantity * item.purchaseprice).toFixed(2));
      }
      this.validateItemDates(item);
      if (!this.showItemPanel) this.calcRow(item);
    },
    validateItemDates(item) {
      if (item.manufacturedate && item.expirydate && item.expirydate < item.manufacturedate) {
        item.expirydate = "";
        Swal.fire({
          icon: "warning",
          title: "Invalid Expiry Date",
          text: "Expiry date cannot be earlier than manufacture date.",
        });
      }
    },

    handleKeydown(event) {
      if (event.key === "Escape") {
        if (this.showItemPanel) {
          this.minimizeItemPanel();
          return;
        }
        if (this.showVendorModal) {
          this.closeVendorModal();
          return;
        }
        this.showVendorResults = false;
        return;
      }
      if (
        this.$route.path === "/addpurchase" &&
        event.ctrlKey &&
        (event.key === "m" || event.key === "M")
      ) {
        event.preventDefault();
        this.openAddItemPanel();
      }
      if (
        this.$route.path === "/addpurchase" &&
        event.ctrlKey &&
        (event.key === "s" || event.key === "S")
      ) {
        event.preventDefault();
        this.submitForms();
      }
    },

    // ── Fetch shop state code for inter-state detection ──
    async fetchShopStateCode() {
      try {
        const res = await axios.get('/v1/company-settings/tax-info');
        this.shopStateCode = res.data?.shop_state_code || null;
      } catch (e) {
        console.error('Error fetching shop state code:', e);
      }
    },

    calcRow(item) {
      const qty     = parseFloat(item.quantity)      || 0;
      const price   = parseFloat(item.purchaseprice) || 0;
      const discPct = Math.min(100, Math.max(0, parseFloat(item.discount) || 0));
      const taxPct  = Math.min(100, Math.max(0, parseFloat(item.taxpercent) || 0));
      item.discount = discPct;
      item.taxpercent = taxPct;

      const base = item.amountBased
        ? parseFloat(item.totalPurchaseAmount) || 0
        : qty * price;
      const discAmt  = (base * discPct) / 100;
      const afterDiscount = base - discAmt;
      item.baseamount = parseFloat(base.toFixed(2));
      item.discountvalue = parseFloat(discAmt.toFixed(2));

      if (item.taxtype === "EXEMPT" || item.taxtype === "NONGST") {
        item.taxablevalue = parseFloat(afterDiscount.toFixed(2));
        item.cgst = 0;
        item.sgst = 0;
        item.igst = 0;
        item.taxamount = 0;
        item.rowtotal = parseFloat(afterDiscount.toFixed(2));
        this.calcSummary();
        return;
      }

      let taxableValue, totalTax;
      if (item.istaxinclusive) {
        taxableValue = parseFloat((afterDiscount / (1 + taxPct / 100)).toFixed(2));
        totalTax     = parseFloat((afterDiscount - taxableValue).toFixed(2));
      } else {
        taxableValue = parseFloat(afterDiscount.toFixed(2));
        totalTax     = parseFloat((taxableValue * taxPct / 100).toFixed(2));
      }

      if (item.taxtype === "IGST") {
        item.cgst = 0;
        item.sgst = 0;
        item.igst = parseFloat(totalTax.toFixed(2));
      } else {
        item.cgst = parseFloat((totalTax / 2).toFixed(2));
        item.sgst = parseFloat((totalTax / 2).toFixed(2));
        item.igst = 0;
      }

      item.taxablevalue = taxableValue;
      item.taxamount    = parseFloat(totalTax.toFixed(2));
      item.rowtotal     = item.istaxinclusive
        ? parseFloat(afterDiscount.toFixed(2))
        : parseFloat((taxableValue + totalTax).toFixed(2));

      this.calcSummary();
    },

    calcSummary() {
      const totalAmount  = this.items.reduce((s, i) => s + (parseFloat(i.rowtotal)   || 0), 0);
      const taxableValue = this.items.reduce((s, i) => s + (parseFloat(i.taxablevalue)|| 0), 0);
      const cgst         = this.items.reduce((s, i) => s + (parseFloat(i.cgst)        || 0), 0);
      const sgst         = this.items.reduce((s, i) => s + (parseFloat(i.sgst)        || 0), 0);
      const igst         = this.items.reduce((s, i) => s + (parseFloat(i.igst)        || 0), 0);
      const taxAmount    = this.items.reduce((s, i) => s + (parseFloat(i.taxamount)   || 0), 0);
      const disc  = parseFloat(this.header.discountamount) || 0;
      const round = parseFloat(this.header.roundoff)       || 0;
      const miscellaneous = this.purchaseExpenseAmount();
      this.header.miscellaneous_amount = miscellaneous;
      this.summary = {
        totalAmount:  parseFloat(totalAmount.toFixed(2)),
        taxableValue: parseFloat(taxableValue.toFixed(2)),
        cgst:         parseFloat(cgst.toFixed(2)),
        sgst:         parseFloat(sgst.toFixed(2)),
        igst:         parseFloat(igst.toFixed(2)),
        taxAmount:    parseFloat(taxAmount.toFixed(2)),
        netAmount:    parseFloat((totalAmount - disc + round + miscellaneous).toFixed(2)),
      };
      if (this.isCreditPurchase && (parseFloat(this.header.paid_amount) || 0) > this.summary.netAmount) {
        this.header.paid_amount = this.summary.netAmount;
      }
    },
    linePurchaseBase(item) {
      return (parseFloat(item.quantity) || 0) * (parseFloat(item.purchaseprice) || 0);
    },
    lineStockQty(item) {
      return (parseFloat(item.quantity) || 0) + (parseFloat(item.freequantity) || 0);
    },
    netPurchaseRate(item) {
      const ptr = parseFloat(item.purchaseprice) || 0;
      const stockQty = this.lineStockQty(item);
      const totalBase = this.items.reduce((sum, row) => sum + this.linePurchaseBase(row), 0);
      const otherCost = this.purchaseExpenseAmount();
      const lineBase = this.linePurchaseBase(item);
      if (!stockQty || !totalBase || !otherCost) return ptr;
      const lineOtherCost = otherCost * (lineBase / totalBase);
      return parseFloat(((lineBase + lineOtherCost) / stockQty).toFixed(2));
    },
    purchaseExpenseAmount() {
      return parseFloat(this.header.purchase_expense?.amount) || 0;
    },

    removeItem(index) {
      this.items.splice(index, 1);
      this.calcSummary();
    },

    async fetchVendorListFromApi(search = "") {
      try {
        const params = { limit: 10 };
        if (search) params.search = search;
        const res = await axios.get("/v1/vendors", { params });
        this.vendorListFromApi = (res.data || []).map((vendor) => ({
          ...vendor,
          vendorlabel: this.buildVendorLabel(vendor),
        }));
      } catch (e) {
        console.error("Error fetching vendors:", e);
      }
    },
    async fetchCategoryListFromApi() {
      try {
        const res = await axios.get("/v1/category");
        this.categoriesFromApi = res.data || [];
      } catch (e) {
        console.error("Error fetching categories:", e);
      }
    },
    scheduleInvoiceCheck() {
      clearTimeout(this.invoiceCheckTimer);
      this.invoiceCheckTimer = setTimeout(async () => {
        this.invoiceDuplicate = await findDuplicateSupplierInvoice(this.header.vendor, this.header.invoiceno, null);
      }, 400);
    },
    openDuplicatePurchase() {
      if (!this.invoiceDuplicate?.purchaseid) return;
      const route = this.$router.resolve({ path: "/purchase-details", query: { id: this.invoiceDuplicate.purchaseid } });
      window.open(route.href, "_blank", "noopener");
    },
    postData(payload) {
      axios.post("/v1/purchases", payload)
        .then(() => {
          Swal.fire({ icon: "success", title: "Purchase Created", text: "Saved successfully.", timer: 1800, showConfirmButton: false });
          this.$router.push({ path: "/purchaselist" });
        })
        .catch((err) => {
          Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.detail || "Something went wrong." });
        });
    },

    async submitForms() {
      if (!this.header.vendor) {
        Swal.fire({ icon: "warning", title: "Supplier Required", text: "Please select a supplier." }); return;
      }
      if (!this.items.length) {
        Swal.fire({ icon: "warning", title: "No Items", text: "Add at least one line item." }); return;
      }
      if (this.items.some(i => !i.itemid)) {
        Swal.fire({ icon: "warning", title: "Item Required", text: "Please select an item for every row." }); return;
      }
      if (this.items.some(i => parseFloat(i.quantity) <= 0)) {
        Swal.fire({ icon: "warning", title: "Invalid Quantity", text: "Quantity must be greater than 0." }); return;
      }
      if (this.items.some(i => i.manufacturedate && i.expirydate && i.expirydate < i.manufacturedate)) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Item Dates",
          text: "Expiry date cannot be earlier than manufacture date.",
        }); return;
      }
      const miscellaneous = this.purchaseExpenseAmount();
      if (miscellaneous < 0) {
        Swal.fire({ icon: "warning", title: "Invalid Amount", text: "Purchase expense cannot be negative." }); return;
      }
      if (miscellaneous > 0 && !this.header.purchase_expense.expense_type) {
        Swal.fire({ icon: "warning", title: "Expense Type Required", text: "Please select an expense type." }); return;
      }
      if (this.isCreditPurchase) {
        const paidAmount = parseFloat(this.header.paid_amount) || 0;
        if (paidAmount < 0) {
          Swal.fire({ icon: "warning", title: "Invalid Amount", text: "Paid amount cannot be negative." }); return;
        }
        if (paidAmount > this.summary.netAmount) {
          Swal.fire({ icon: "warning", title: "Invalid Amount", text: "Paid amount cannot exceed net amount." }); return;
        }
      }

      const result = await Swal.fire({
        title: "Are you sure?", text: "Do you want to submit this purchase?",
        icon: "warning", showCancelButton: true,
        confirmButtonColor: "#3085d6", cancelButtonColor: "#d33",
        confirmButtonText: "Yes, submit it!",
      });
      if (!result.isConfirmed) return;

      const invoiceDatetime =
        moment(this.header.invoicedate).format("YYYY-MM-DD");
        

      const payload = {
        vendor:         this.header.vendor,
        billno:         null,
        invoiceno:      this.header.invoiceno || null,
        invoicedate:    invoiceDatetime,
        transportmode:  this.header.transportmode || null,
        vehicleno:      this.header.vehicleno || null,
        paymentmode:    this.header.paymentmode,
        locationid:     this.header.locationid || null,
        totalamount:    this.summary.totalAmount,
        discountamount: this.header.discountamount,
        taxamount:      this.summary.taxAmount,
        roundoff:       this.header.roundoff,
        miscellaneous_amount: miscellaneous,
        purchase_expense: miscellaneous > 0 ? {
          expense_type: this.header.purchase_expense.expense_type,
          amount: miscellaneous,
          allocation_method: this.header.purchase_expense.allocation_method || "VALUE",
          remarks: this.header.purchase_expense.remarks || null,
        } : null,
        netamount:      this.summary.netAmount,
        is_credit:      this.isCreditPurchase,
        due_date:       this.isCreditPurchase ? (this.header.due_date || null) : null,
        paid_amount:    this.isCreditPurchase ? (parseFloat(this.header.paid_amount) || 0) : this.summary.netAmount,
        remarks:        this.header.remarks,
        isinterstate:   this.isInterState,
        vendorgstin:     this.selectedVendorGSTIN || null,
        placeofsupply:   this.isInterState ? (this.selectedVendorGSTIN?.substring(0, 2) || null) : this.shopStateCode,
        items: this.items.map(i => ({
          itemid:          i.itemid,
          locationid:      this.header.locationid || null,
          quantity:        i.quantity,
          freequantity:    i.freequantity,
          purchaseprice:   i.purchaseprice,
          mrp:             i.mrp,
          wholesaleprice:  i.wholesaleprice,
          discount:        i.discount,
          taxtype:         i.taxtype,
          taxpercent:      i.taxpercent,
          taxablevalue:    i.taxablevalue,
          cgst:            i.cgst,
          sgst:            i.sgst,
          igst:            i.igst,
          istaxinclusive:  i.istaxinclusive,
          taxamount:       i.taxamount,
          batchno:         i.batchno,
          manufacturedate: i.manufacturedate || null,
          expirydate:      i.expirydate      || null,
        })),
      };
      if (!(await confirmIfDuplicateInvoice(this.header.vendor, this.header.invoiceno))) return;
      this.postData(payload);
    },
  },
};
</script>


<style scoped>
.purchase-entry-card,
.purchase-entry-body,
.purchase-workspace {
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.purchase-entry-card {
  border: 0.5px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: none;
}
.purchase-workspace { display: flex; flex-direction: column; gap: 16px; }

.purchase-header-card,
.line-items-card {
  background: #fff;
  border: 0.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
}
.purchase-header-row-1 {
  display: grid;
  grid-template-columns: 2fr repeat(5, minmax(130px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}
.purchase-header-row-2 {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 12px;
}
.ph-label { display: block; font-size: 12px; font-weight: 600; color: #6b7280; margin-bottom: 6px; }
.ph-req { color: #F97316; }
.ph-input {
  height: 38px; font-size: 14px;
  border: 0.5px solid #d1d5db; border-radius: 8px; background: #fff;
}
.ph-input:focus { border-color: #F97316; box-shadow: none; outline: none; }
.ph-input-barcode { border-style: dashed; }

.line-items-table-wrap { border: 0.5px solid #e5e7eb; border-radius: 10px; overflow-x: auto; }
.line-items-table { width: 100%; min-width: 1180px; border-collapse: collapse; }
.line-items-table th {
  padding: 10px 12px; font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.04em; color: #6b7280; background: #f9fafb;
  border-bottom: 0.5px solid #e5e7eb; text-align: left;
}
.line-items-table td {
  padding: 12px; font-size: 14px; color: #111827;
  border-bottom: 0.5px solid #f3f4f6; vertical-align: middle;
}
.line-items-table tr:last-child td { border-bottom: none; }
.col-num { width: 48px; text-align: center; color: #9ca3af; }
.col-actions { width: 96px; text-align: right; white-space: nowrap; }
.col-total { font-weight: 600; }
.item-name-cell { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.amount-badge {
  display: inline-flex; padding: 2px 8px; border-radius: 999px;
  background: #fff7ed; color: #c2410c; border: 0.5px solid #fdba74;
  font-size: 10px; font-weight: 700; text-transform: lowercase;
}
.line-items-empty { padding: 40px 20px; text-align: center; color: #6b7280; }
.line-items-empty p { margin: 0 0 6px; font-size: 15px; font-weight: 600; color: #374151; }
.icon-action-btn {
  width: 32px; height: 32px; border: 0.5px solid #e5e7eb; border-radius: 8px;
  background: #fff; color: #6b7280; cursor: pointer; margin-left: 4px;
}
.icon-action-btn:hover { border-color: #F97316; color: #F97316; }
.icon-action-danger:hover { border-color: #ef4444; color: #ef4444; }
.add-item-btn {
  margin-top: 12px; display: inline-flex; align-items: center;
  padding: 8px 16px; border: 0.5px dashed #fdba74; border-radius: 8px;
  background: #fff; color: #F97316; font-size: 14px; font-weight: 600; cursor: pointer;
}
.add-item-btn:hover { background: #fff7ed; }

.purchase-extra-cost-card {
  margin-top: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 14px;
}
.extra-cost-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}
.extra-cost-title { margin: 0; font-size: 0.96rem; font-weight: 700; color: #1f2937; }
.extra-cost-subtitle { margin: 4px 0 0; color: #6b7280; font-size: 0.8rem; }
.extra-cost-chip {
  flex: 0 0 auto;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid #fed7aa;
  background: #fff7ed;
  color: #c2410c;
  font-size: 0.72rem;
  font-weight: 700;
}
.extra-cost-grid { display: grid; grid-template-columns: 180px 220px minmax(0, 1fr); gap: 14px; align-items: start; }
.extra-cost-input { text-align: right; }
.extra-cost-remarks .remarks-input { min-height: 70px; }

.item-sheet-backdrop {
  position: fixed; inset: 0; z-index: 1100;
  background: rgba(17, 24, 39, 0.4);
  display: flex; align-items: flex-end; justify-content: center;
}
.item-sheet {
  width: min(100%, 920px); max-height: 92vh; background: #fff;
  border-radius: 16px 16px 0 0; border: 0.5px solid #e5e7eb;
  display: flex; flex-direction: column;
}
.item-sheet-handle {
  width: 40px; height: 4px; border-radius: 999px;
  background: #d1d5db; margin: 10px auto 0;
}
.item-sheet-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 20px; border-bottom: 0.5px solid #e5e7eb;
}
.item-sheet-header h3 { margin: 0; font-size: 18px; font-weight: 700; color: #111827; }
.item-sheet-close {
  width: 32px; height: 32px; border: 0.5px solid #e5e7eb; border-radius: 8px;
  background: #fff; color: #6b7280; cursor: pointer;
}
.item-sheet-body {
  padding: 16px 20px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 14px;
}
.item-sheet-footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 20px; border-top: 0.5px solid #e5e7eb;
}
.sheet-label { display: block; font-size: 12px; font-weight: 600; color: #6b7280; margin-bottom: 6px; }
.sheet-input {
  height: 38px; font-size: 14px;
  border: 0.5px solid #d1d5db; border-radius: 8px; background: #fff;
}
.sheet-input:focus { border-color: #F97316; box-shadow: none; outline: none; }
.sheet-input-highlight { border-color: #F97316; }
.sheet-input-readonly { background: #f9fafb; border-style: dashed; color: #6b7280; }
.sheet-field-row-2 { display: grid; grid-template-columns: 1fr 2fr; gap: 12px; }
.sheet-grid-4 { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.sheet-grid-3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.sheet-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; align-items: end; }
.sheet-grid-shared { margin-top: 4px; }
.sheet-field-inclusive { display: flex; align-items: center; min-height: 38px; }
.sheet-label-check {
  display: inline-flex; align-items: center; gap: 8px;
  margin-bottom: 0; cursor: pointer;
}
.panel-item-search { position: relative; }
.panel-item-results {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 20;
  background: #fff; border: 0.5px solid #e5e7eb; border-radius: 8px;
  max-height: 220px; overflow-y: auto;
}
.panel-item-option {
  width: 100%; border: 0; background: transparent; text-align: left;
  padding: 10px 12px; font-size: 14px; color: #374151; cursor: pointer;
}
.panel-item-option:hover, .panel-item-option.active { background: #fff7ed; color: #c2410c; }
.panel-item-msg { padding: 12px; font-size: 13px; color: #9ca3af; text-align: center; }
.amount-toggle-row {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 12px 14px; border: 0.5px solid #e5e7eb; border-radius: 10px; background: #fafafa;
}
.amount-toggle-copy { display: flex; flex-direction: column; gap: 4px; }
.amount-toggle-label { font-size: 14px; font-weight: 600; color: #111827; }
.amount-toggle-desc { font-size: 12px; color: #6b7280; }
.amount-toggle-switch { position: relative; display: inline-flex; cursor: pointer; }
.amount-toggle-switch input { position: absolute; opacity: 0; width: 0; height: 0; }
.amount-toggle-track {
  width: 44px; height: 24px; border-radius: 999px; background: #d1d5db; position: relative;
}
.amount-toggle-thumb {
  position: absolute; top: 3px; left: 3px; width: 18px; height: 18px;
  border-radius: 50%; background: #fff; transition: transform 0.2s, background 0.2s;
}
.amount-toggle-switch input:checked + .amount-toggle-track { background: #ffedd5; }
.amount-toggle-switch input:checked + .amount-toggle-track .amount-toggle-thumb {
  transform: translateX(20px); background: #F97316;
}
.amount-info-banner {
  padding: 10px 12px; border-radius: 8px;
  border: 0.5px solid #fdba74; background: #fff7ed; color: #c2410c; font-size: 13px;
}
.calc-pills { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.calc-pill {
  padding: 10px 12px; border: 0.5px solid #e5e7eb; border-radius: 10px;
  background: #fafafa; display: flex; flex-direction: column; gap: 4px;
}
.calc-pill span { font-size: 11px; color: #6b7280; text-transform: uppercase; font-weight: 600; }
.calc-pill strong { font-size: 15px; color: #111827; }
.calc-pill-net { background: #fff7ed; border-color: #fdba74; }
.calc-pill-net strong { color: #c2410c; }
.sheet-btn-cancel {
  height: 40px; padding: 0 18px; border: 0.5px solid #d1d5db; border-radius: 8px;
  background: #fff; color: #374151; font-weight: 600; cursor: pointer;
}
.sheet-btn-primary {
  height: 40px; padding: 0 18px; border: none; border-radius: 8px;
  background: #F97316; color: #fff; font-weight: 700; cursor: pointer;
}
.sheet-btn-primary:hover { background: #ea580c; }

.lookup-control-row { display: flex; align-items: stretch; gap: 10px; }
.lookup-box { position: relative; flex: 1 1 auto; }
.lookup-results {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0; z-index: 30;
  background: #fff; border: 0.5px solid #e5e7eb; border-radius: 8px;
  max-height: 280px; overflow-y: auto;
}
.lookup-result-item {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.lookup-result-item:hover { background: #fff7ed; }
.lookup-result-title { color: #111827; font-weight: 600; }
.lookup-result-meta,
.lookup-empty-state { color: #6b7280; font-size: 0.84rem; }
.lookup-empty-state { padding: 12px; }
.inline-add-btn {
  min-height: 38px;
  padding: 0 12px;
  border: 0.5px solid #fdba74;
  border-radius: 8px;
  background: #fff;
  color: #F97316;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  flex: 0 0 auto;
  transition: all 0.2s ease;
}
.inline-add-btn:hover { background: #ffedd5; border-color: #fdba74; }
.inline-add-btn i { font-size: 0.85rem; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
.purchase-entry-body { padding: 18px !important; }
.form-label { font-size: 0.78rem; font-weight: 600; color: #6b7280; margin-bottom: 5px; display: block; }
.form-ctrl  { height: 36px; font-size: 0.875rem; border: 0.5px solid #e5e7eb; border-radius: 8px; padding: 0 10px; }
.form-ctrl:focus { border-color: #F97316; box-shadow: none; outline: none; }
.purchase-bottom-grid + .row.mb-4 { display: none; }
.divider-bold { border-top: 0.5px solid #e5e7eb; margin: 16px 0; }

.purchase-bottom-grid { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 18px; align-items: start; }
.purchase-bottom-grid + .row.mb-4 { display: none; }
.notes-card                { border: 1px solid #e7e5e4; border-radius: 12px; background: #fff; padding: 14px; }
.notes-card-head           { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 10px; }
.notes-card-title          { margin: 0; font-size: 0.95rem; font-weight: 700; color: #1f2937; }
.notes-card-meta           { font-size: 0.76rem; color: #9ca3af; }
.remarks-input             { min-height: 96px; padding-top: 10px; resize: vertical; }
.purchase-totals-panel     { position: sticky; top: 88px; }
.totals-box                { border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; background: #fff; }
.totals-box-head           { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 14px; border-bottom: 1px solid #f3f4f6; background: #fffaf5; }
.totals-box-title          { margin: 0; font-size: 0.96rem; font-weight: 700; color: #1f2937; }
.totals-box-subtitle       { margin: 4px 0 0; font-size: 0.76rem; color: #6b7280; }
.totals-box-badge          { padding: 5px 10px; border-radius: 999px; background: #fff; border: 1px solid #fed7aa; color: #c2410c; font-size: 0.72rem; font-weight: 700; }
.totals-row                { display: flex; justify-content: space-between; align-items: center; padding: 8px 14px; border-bottom: 1px solid #f3f4f6; gap: 10px; }
.totals-row:last-child     { border-bottom: none; }
.totals-label              { font-size: 0.83rem; font-weight: 500; color: #6b7280; white-space: nowrap; flex-shrink: 0; }
.totals-input              { width: 130px; height: 28px; text-align: right; font-size: 0.85rem; padding: 2px 8px; border: 1px solid #e5e7eb; border-radius: 5px; outline: none; }
.totals-input:focus        { border-color: #fb923c; }
.totals-input-ro           { background: #f9fafb !important; color: #374151 !important; cursor: default; }
.totals-net-row            { background: #fff7f0; padding: 12px 14px; border-top: 2px solid #fb923c !important; }
.totals-net-label          { font-size: 0.95rem; font-weight: 700; color: #ea580c; }
.totals-net-val            { font-size: 1.2rem; font-weight: 800; color: #ea580c; }
.credit-payment-box        { margin-top: 10px; padding-top: 10px; border-top: 1px solid #e5e7eb; }
.credit-status-chip        { display: inline-flex; align-items: center; min-height: 26px; padding: 2px 10px; border-radius: 6px; background: #fff7ed; color: #c2410c; font-size: 0.78rem; font-weight: 800; }

.btn-submit { padding: 9px 24px; background: #F97316; color: #fff; border: none; border-radius: 8px; font-size: 0.9rem; font-weight: 700; cursor: pointer; }
.btn-submit:hover { background: #ea580c; }

.btn-cancel-lnk       { padding: 9px 20px; background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; border-radius: 7px; font-size: 0.9rem; font-weight: 600; text-decoration: none; display: inline-flex; align-items: center; transition: background 0.12s; }
.btn-cancel-lnk:hover { background: #e5e7eb; }
.purchase-footer-actions { display: flex; gap: 10px; align-items: center; }

.interstate-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
}
.interstate-igst {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fbbf24;
}
.interstate-intra {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #34d399;
}

.quick-create-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.quick-create-modal {
  width: min(100%, 680px);
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.22);
  padding: 22px;
}
.quick-create-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}
.quick-create-modal-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
}
.quick-create-modal-subtitle {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 0.88rem;
}
.quick-create-close {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  background: #f3f4f6;
  color: #374151;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 1200px) {
  .purchase-header-row-1 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .purchase-header-row-2 { grid-template-columns: 1fr; }
  .sheet-grid-4, .calc-pills { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .purchase-bottom-grid { grid-template-columns: 1fr; }
  .extra-cost-grid { grid-template-columns: 1fr; }
  .purchase-totals-panel { position: static; }
}

@media (max-width: 767px) {
  .sheet-field-row-2, .sheet-grid-2, .sheet-grid-3, .sheet-grid-4, .calc-pills { grid-template-columns: 1fr; }
  .extra-cost-head { flex-direction: column; }
}

.dup-invoice-warning { display: flex; align-items: flex-start; gap: 6px; margin-top: 6px; padding: 6px 8px; border: 1px solid #fde68a; border-radius: 6px; background: #fffbeb; color: #92400e; font-size: 12px; line-height: 1.35; }
.dup-invoice-warning i { margin-top: 2px; }
.dup-invoice-warning a { color: #c2410c; font-weight: 600; text-decoration: underline; }
.dup-invoice-input { border-color: #f59e0b !important; }
</style>

<style>
/* SweetAlert must sit above the item panel (z-index 1100) */
.swal2-container {
  z-index: 1300 !important;
}
</style>


